import { useTranslation } from "react-i18next";
import { Badge, Button } from "../../../../../../components/ui";
import { getStatusTone, getStatusStyle } from "../utils/helpers";
import type { TrackingLink, ThemeClasses } from "../types";
import { useState } from "react";

interface TrackingLinkDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackingLink: TrackingLink | null;
  themeClasses: ThemeClasses;
}

export function TrackingLinkDetailsModal({
  isOpen,
  onClose,
  trackingLink,
  themeClasses,
}: TrackingLinkDetailsModalProps) {
  const [activeTab, setActiveTab] = useState<"details" | "access">("details");
  const { t } = useTranslation("adminSections");

  if (!isOpen || !trackingLink) return null;

  const formatDate = (date: any) => {
    if (!date) return "N/A";
    try {
      const d = date._seconds ? new Date(date._seconds * 1000) : new Date(date);
      if (isNaN(d.getTime())) return t("trackingLinks.messages.invalidDate");
      return d.toLocaleString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return t("trackingLinks.messages.errorFormattingDate");
    }
  };

  const getStatusText = (status: any) => {
    if (!status) return t("trackingLinks.details.unknownStatus");
    return status.name;
  };

  const getAccessTypeText = (type: string) => {
    switch (type) {
      case "public":
        return t("trackingLinks.accessType.public");
      case "protected":
        return t("trackingLinks.accessType.protected");
      default:
        return type;
    }
  };

  const getActivationTypeText = (type: string) => {
    switch (type) {
      case "immediate":
        return t("trackingLinks.activationType.immediate");
      case "scheduled":
        return t("trackingLinks.activationType.scheduled");
      case "inactive":
        return t("trackingLinks.activationType.inactive");
      default:
        return type;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const uniqueIPs = trackingLink.accessHistory?.length || 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-xl ${themeClasses.cardBg} shadow-2xl`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${themeClasses.cardBorder}`}>
          <h2 className={`text-lg sm:text-xl font-semibold ${themeClasses.text}`}>
            {t("trackingLinks.details.title")}
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText}`}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className={`flex border-b ${themeClasses.cardBorder}`}>
          <button
            onClick={() => setActiveTab("details")}
            className={`px-4 sm:px-6 py-3 text-sm sm:text-base font-medium transition-colors ${
              activeTab === "details"
                ? `text-primary-600 border-b-2 border-primary-600`
                : `${themeClasses.textSecondary} hover:${themeClasses.text}`
            }`}
          >
            {t("trackingLinks.details.generalInfo")}
          </button>
          <button
            onClick={() => setActiveTab("access")}
            className={`px-4 sm:px-6 py-3 text-sm sm:text-base font-medium transition-colors relative ${
              activeTab === "access"
                ? `text-primary-600 border-b-2 border-primary-600`
                : `${themeClasses.textSecondary} hover:${themeClasses.text}`
            }`}
          >
            {t("trackingLinks.details.accessHistory")}
            {trackingLink.accessHistory && trackingLink.accessHistory.length > 0 && (
              <span className="absolute -top-1 -right-2 bg-primary-500 text-on-primary text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {trackingLink.accessHistory.length}
              </span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === "details" ? (
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
                  {t("trackingLinks.details.basicInfo")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.form.nameLabel")}
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{trackingLink.name}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.fields.status")}
                    </label>
                    <div className="mt-1">
                      <Badge
                        tone={getStatusTone(trackingLink.status)}
                        style={getStatusStyle(trackingLink.status)}
                      >
                        {getStatusText(trackingLink.status)}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.fields.accessType")}
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>
                      {getAccessTypeText(trackingLink.accessType)}
                      {trackingLink.hasAccessCode && (
                        <span className="ml-2 text-xs bg-info/15 text-info px-2 py-1 rounded">
                          {t("trackingLinks.filters.protected")}
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.fields.activationType")}
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>
                      {getActivationTypeText(trackingLink.activationType)}
                    </p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.details.totalAccesses")}
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{(trackingLink.accessCount || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.details.assignedUnits")}
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>
                      {trackingLink.unitsCount || 0}{" "}
                      {t("trackingLinks.infoLabels.vehicles")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
                  {t("trackingLinks.details.dates")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.details.createdAt")}
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{formatDate(trackingLink.createdAt)}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.fields.activationDate")}
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{formatDate(trackingLink.activationDate)}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.fields.expirationDate")}
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{formatDate(trackingLink.expirationDate)}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.lastUpdate")}
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{formatDate(trackingLink.updatedAt)}</p>
                  </div>
                </div>
              </div>

              {/* Duration */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
                  {t("trackingLinks.fields.duration")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.details.durationValue")}
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{trackingLink.durationValue || 'N/A'}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.details.durationUnit")}
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>
                      {trackingLink.durationUnit
                        ? t(`trackingLinks.duration.${trackingLink.durationUnit}`)
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* URLs */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
                  {t("trackingLinks.details.urls")}
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.fields.publicUrl")}
                    </label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="text"
                        value={`${window.location.origin}${trackingLink.publicUrl}`}
                        readOnly
                        className={`flex-1 px-3 py-2 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.cardBg}`}
                      />
                      <button
                        onClick={() => copyToClipboard(`${window.location.origin}${trackingLink.publicUrl}`)}
                        className="inline-flex items-center justify-center px-3 py-2 bg-primary-500 hover:bg-primary-400 text-on-primary rounded-lg transition-colors"
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                  </div>
                  { /* <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      Hash Único
                    </label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="text"
                        value={trackingLink.uniqueHash}
                        readOnly
                        className={`flex-1 px-3 py-2 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.cardBg}`}
                      />
                      <button
                        onClick={() => copyToClipboard(trackingLink.uniqueHash)}
                        className="inline-flex items-center justify-center px-3 py-2 bg-primary-500 hover:bg-primary-400 text-on-primary rounded-lg transition-colors"
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                  </div> */}
                </div>
              </div>

              {/* Internal Notes */}
              {trackingLink.internalNotes && (
                <div>
                  <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
                    {t("trackingLinks.fields.internalNotes")}
                  </h3>
                  <div className={`p-4 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
                    <p className={`${themeClasses.text}`}>{trackingLink.internalNotes}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Access Statistics */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
                  {t("trackingLinks.details.accessStats")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`p-4 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
                    <div className={`text-2xl font-bold ${themeClasses.text}`}>
                      {(trackingLink.accessCount || 0).toLocaleString()}
                    </div>
                    <div className={`text-sm ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.details.totalAccesses")}
                    </div>
                  </div>
                  <div className={`p-4 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
                    <div className={`text-2xl font-bold ${themeClasses.text}`}>
                      {uniqueIPs}
                    </div>
                    <div className={`text-sm ${themeClasses.textSecondary}`}>
                      {t("trackingLinks.details.uniqueIps")}
                    </div>
                  </div>
                  { /* <div className={`p-4 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
                    <div className={`text-2xl font-bold ${themeClasses.text}`}>
                      {uniqueIPs > 0 ? Math.round(totalAccesses / uniqueIPs) : 0}
                    </div>
                    <div className={`text-sm ${themeClasses.textSecondary}`}>Promedio por IP</div>
                  </div> */}
                </div>
              </div>

              {/* Access History */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
                  {t("trackingLinks.details.historyByIp")}
                </h3>
                {trackingLink.accessHistory && trackingLink.accessHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={`border-b ${themeClasses.cardBorder}`}>
                          <th className={`text-left py-3 px-4 ${themeClasses.textSecondary}`}>
                            {t("trackingLinks.details.ipAddress")}
                          </th>
                          <th className={`text-left py-3 px-4 ${themeClasses.textSecondary}`}>
                            {t("trackingLinks.details.accesses")}
                          </th>
                          <th className={`text-left py-3 px-4 ${themeClasses.textSecondary}`}>
                            {t("trackingLinks.details.firstAccess")}
                          </th>
                          <th className={`text-left py-3 px-4 ${themeClasses.textSecondary}`}>
                            {t("trackingLinks.details.lastAccess")}
                          </th>
                          <th className={`text-left py-3 px-4 ${themeClasses.textSecondary}`}>
                            {t("trackingLinks.details.userAgent")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {trackingLink.accessHistory.map((entry, index) => (
                          <tr key={index} className={`border-b ${themeClasses.cardBorder}`}>
                            <td className={`py-3 px-4 ${themeClasses.text}`}>
                              <div className="flex items-center gap-2">
                                <i className="ri-computer-line text-content-faint"></i>
                                <span className="font-mono text-sm">{entry.ip}</span>
                              </div>
                            </td>
                            <td className={`py-3 px-4 ${themeClasses.text}`}>
                              <span className="px-2 py-1 bg-info/15 text-info rounded-full text-xs font-medium">
                                {entry.accessCount}
                              </span>
                            </td>
                            <td className={`py-3 px-4 ${themeClasses.text} text-sm`}>
                              {formatDate(entry.firstAccess)}
                            </td>
                            <td className={`py-3 px-4 ${themeClasses.text} text-sm`}>
                              {formatDate(entry.lastAccess)}
                            </td>
                            <td className={`py-3 px-4 ${themeClasses.text} text-sm max-w-xs truncate`}>
                              <span title={entry.userAgent}>
                                {entry.userAgent.length > 50 
                                  ? `${entry.userAgent.substring(0, 50)}...` 
                                  : entry.userAgent}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className={`text-center py-8 ${themeClasses.textSecondary}`}>
                    <i className="ri-eye-off-line text-4xl mb-3"></i>
                    <p>{t("trackingLinks.details.noRecords")}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={`flex gap-3 p-4 sm:p-6 border-t ${themeClasses.cardBorder}`}>
          <Button variant="outline" shape="pill" fullWidth onClick={onClose}>
            {t("trackingLinks.details.close")}
          </Button>
        </div>
      </div>
    </div>
  );
}
